import {useEffect, useState} from 'react'
import {Breadcrumb, Button, Col, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import {useLazyQuery} from '@apollo/client'
import {useParams, Link} from 'react-router-dom'
import {toast} from 'react-toastify'
import moment from 'moment'
import {GET_ALL_PDF_ENVELOPES, GET_ONE_PDF_DOCUMENT_TEMPLATE} from '../../gql/queries/pdfEnvelopesApiQueries'
import {NodePdfEnvelope} from '../../types/pdfEnvelopesApi'
import {parseId} from '../../helpers'
import {useDisclourse} from '../../hooks/useDisclourse'
import {BadgeStatus} from '../pdfEnvelopes/components/BadgeStatus'
import {ModalEnvelopePreview} from '../pdfEnvelopes/components/ModalEnvelopePreview'

export const PdfEnvelopeEntriesPage = () => {
  const {id} = useParams<{id: string}>()

  const [gqlGetTemplate, {data: templateData}] = useLazyQuery(GET_ONE_PDF_DOCUMENT_TEMPLATE, {
    fetchPolicy: 'no-cache',
  })

  const [gqlGetEnvelopes, {data, loading, error, refetch}] = useLazyQuery(GET_ALL_PDF_ENVELOPES, {
    fetchPolicy: 'no-cache',
  })

  const [filterStatus, setFilterStatus] = useState('')
  const [selectedEnvelopeId, setSelectedEnvelopeId] = useState<string>('')
  const {isOpen, onOpen, onClose} = useDisclourse()

  const templateName = templateData?.pdfDocumentTemplateById?.name ?? id

  useEffect(() => {
    if (!id) return
    const numericId = parseId(id) || id
    gqlGetTemplate({variables: {id: numericId}})
    gqlGetEnvelopes({variables: {templateId: numericId, status: filterStatus || null}})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const envelopes: NodePdfEnvelope[] =
    data?.allPdfEnvelopes ?? []

  const handleSearch = () => {
    if (!id) return
    gqlGetEnvelopes({variables: {templateId: parseId(id) || id, status: filterStatus || null}})
  }

  const signersSummary = (envelope: NodePdfEnvelope) => {
    const total = envelope.signers?.length ?? 0
    const signed = envelope.signers?.filter((s) => s.status === 'SIGNED').length ?? 0
    return `${signed}/${total} signed`
  }

  return (
    <div className='signatureForms-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/pdf-envelopes'}} className='fs-3'>
            PDF Signing
          </Breadcrumb.Item>
          <Breadcrumb.Item active className='fs-3'>
            {templateName}
          </Breadcrumb.Item>
        </Breadcrumb>
      </header>

      <Row className='signatureForms'>
        <Col className='container'>
          <div className='signatureForms-container'>
            <div className='title-header'>
              <h3>Envelopes — {templateName}</h3>
            </div>

            <div className='filters-header'>
              <InputGroup>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value=''>All statuses</option>
                  <option value='PENDING'>Pending</option>
                  <option value='PARTIAL'>Partial</option>
                  <option value='COMPLETED'>Completed</option>
                  <option value='VOIDED'>Voided</option>
                  <option value='EXPIRED'>Expired</option>
                </Form.Select>
                <Button variant='outline-success' style={{border: '1px solid #dbdfe9'}} onClick={handleSearch}>
                  Filter
                </Button>
              </InputGroup>
            </div>

            <div className='table-responsive table table-striped table-hover'>
              <table className='table-scrollable table-scroll table table-striped table-hover' style={{whiteSpace: 'nowrap'}}>
                <thead>
                  <tr>
                    <th className='fw-bold'>Title</th>
                    <th className='fw-bold'>Status</th>
                    <th className='fw-bold'>Signers</th>
                    <th className='fw-bold'>Order</th>
                    <th className='fw-bold'>Sent by</th>
                    <th className='fw-bold'>Created</th>
                    <th className='fw-bold'>Actions</th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan={7}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {envelopes.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex flex-center' style={{height: '20vh'}}>
                            No envelopes for this template.
                          </div>
                        </td>
                      </tr>
                    )}
                    {envelopes.map((env) => (
                      <tr key={env.id}>
                        <td className='fw-semibold'>{env.title}</td>
                        <td><BadgeStatus state={env.status} /></td>
                        <td>
                          <span>{signersSummary(env)}</span>
                          <div style={{fontSize: 11, color: '#888'}}>
                            {env.signers?.map((s) => (
                              <span key={s.id} className='me-2'>
                                {s.recipientName} <BadgeStatus state={s.status} />
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span style={{fontSize: 12}}>
                            {env.signingOrder === 'SEQUENTIAL' ? '⛓ Sequential' : '⚡ Parallel'}
                          </span>
                        </td>
                        <td>
                          {env.user
                            ? `${env.user.firstName} ${env.user.lastName}`.trim()
                            : '—'}
                        </td>
                        <td>{env.created ? moment(env.created).format('DD MMM YY HH:mm') : '—'}</td>
                        <td>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            style={{border: '1px solid #3e97ff'}}
                            onClick={() => {
                              setSelectedEnvelopeId(env.id)
                              onOpen()
                            }}
                          >
                            <i className='bi bi-eye' /> View detail
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && selectedEnvelopeId && (
        <ModalEnvelopePreview
          envelopeId={selectedEnvelopeId}
          isOpen={isOpen}
          onClose={onClose}
          refetch={refetch}
        />
      )}
    </div>
  )
}
